import * as React from 'react';
import {useRef, useState} from 'react';
import classnames from 'classnames';

import {isFunction} from 'features/services/utils/helpers';

import './_filterPopout.scss';

import {Button, ButtonColor, ButtonSize, ButtonState, ButtonType} from 'components/atoms/LinksAndButtons/Button';
import {useTrapFocus} from 'components/hooks/useTrapFocus';
import {useOutsideClick} from 'components/hooks/useOutsideClick';
import { ModalType, useModal } from 'features/modal/hooks/useModal';
import { ResidenceFilterModal } from 'components/modals/General/ResidenceFilterModal';

interface Props {
    children: React.ReactNode | ((closeContent: () => void) => React.ReactElement);
    buttonTitle: string;
    active?: boolean;
    dataTestId?: string;
    contentClass?: string;
    small?: boolean;
}

export const FilterPopout: React.FunctionComponent<Props> = ({children, buttonTitle, active, dataTestId, contentClass, small}) => {
    const [contentVisible, setContentVisible] = useState<boolean>(false);
    const filterStartRef = useRef<HTMLButtonElement>(null);
    const filterContentRef = useRef<HTMLDivElement>(null);
    
    const { open } = useModal();

    const contentClasses = classnames('filter-popout__content', contentClass, {
        'filter-popout__content--small': small,
    });

    const toggleContent = () => {
        setContentVisible((prev) => !prev);
    };

    const closeContent = () => {
        setContentVisible(false);
    };

    useTrapFocus({
        triggerElementRef: filterStartRef,
        firstFocusElement: filterStartRef,
        lastFocusElement: filterStartRef,
        wrapperElement: filterContentRef,
        onCloseCallback: closeContent,
        trigger: contentVisible,
    });

    useOutsideClick({
        refs: [filterContentRef],
        excludeRefs: [filterStartRef],
        callback: closeContent,
    });


    return (
        <div className="filter-popout" >
            <Button
                onClick={toggleContent}
                buttonState={contentVisible
                    ? ButtonState.ACTIVE
                    : ButtonState.DEFAULT
                }
                foldableActive={active}
                data-test-id={dataTestId}
                ariaExpanded={contentVisible}
                ariaControls={contentVisible ? buttonTitle : undefined}
                ref={filterStartRef}
                buttonColor={ButtonColor.DEFAULT}
                buttonSize={ButtonSize.DEFAULT}
                buttonType={ButtonType.FOLD}
                id={'filter-' + dataTestId}
            >
                {buttonTitle}
            </Button>

            {contentVisible &&
                <div
                    className={contentClasses}
                    id={dataTestId}
                    ref={filterContentRef}
                >
                    {(isFunction(children) ? children(closeContent) : children)}
                </div>
            }
        </div>
    );
};
